import { safeApi, apiClient } from '../../../utils/axiosWithAuth';
import { 
  ApiRole, 
  ApiPermission, 
  CreateRoleRequest, 
  UpdateRoleRequest,
  CreatePermissionRequest,
  UpdatePermissionRequest,
  RoleQueryParams,
  PermissionQueryParams,
  PermissionsGroupedByCategory,
  CheckPermissionRequest,
  CheckPermissionResponse
} from '../types/api-types';
import { ApiResponse } from '../../../utils/axiosWithAuth';

// ===============================
// ROLE API FUNCTIONS
// ===============================

export const getAllRolesAPI = (params?: RoleQueryParams) =>
  safeApi<ApiRole[]>(() => {
    const queryParams = new URLSearchParams();
    if (params?.includeDeleted !== undefined) queryParams.append('includeDeleted', params.includeDeleted.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.includeSystemRoles !== undefined) queryParams.append('includeSystemRoles', params.includeSystemRoles.toString());
    
    return apiClient.get<ApiResponse<ApiRole[]>>(`/admin/roles-permissions/roles?${queryParams.toString()}`);
  });

export const getRoleByIdAPI = (uuid: string) =>
  safeApi<ApiRole>(() =>
    apiClient.get<ApiResponse<ApiRole>>(`/admin/roles-permissions/roles/${uuid}`)
  );

export const createRoleAPI = (roleData: CreateRoleRequest) =>
  safeApi<ApiRole>(() =>
    apiClient.post<ApiResponse<ApiRole>>('/admin/roles-permissions/roles', roleData)
  );

export const updateRoleAPI = (uuid: string, roleData: UpdateRoleRequest) =>
  safeApi<ApiRole>(() =>
    apiClient.put<ApiResponse<ApiRole>>(`/admin/roles-permissions/roles/${uuid}`, roleData)
  );

export const deleteRoleAPI = (uuid: string) =>
  safeApi<void>(() =>
    apiClient.delete<ApiResponse<void>>(`/admin/roles-permissions/roles/${uuid}`)
  );

export const restoreRoleAPI = (uuid: string) =>
  safeApi<ApiRole>(() =>
    apiClient.post<ApiResponse<ApiRole>>(`/admin/roles-permissions/roles/${uuid}/restore`)
  );

export const getRolePermissionsAPI = (uuid: string) =>
  safeApi<ApiPermission[]>(() =>
    apiClient.get<ApiResponse<ApiPermission[]>>(`/admin/roles-permissions/roles/${uuid}/permissions`)
  );

// ===============================
// PERMISSION API FUNCTIONS
// ===============================

export const getAllPermissionsAPI = (params?: PermissionQueryParams) =>
  safeApi<ApiPermission[]>(() => {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.includeDeleted !== undefined) queryParams.append('includeDeleted', params.includeDeleted.toString());
    if (params?.includeInactive !== undefined) queryParams.append('includeInactive', params.includeInactive.toString());
    if (params?.search) queryParams.append('search', params.search);
    
    return apiClient.get<ApiResponse<ApiPermission[]>>(`/admin/roles-permissions/permissions?${queryParams.toString()}`);
  });

export const getPermissionByIdAPI = (uuid: string) =>
  safeApi<ApiPermission>(() =>
    apiClient.get<ApiResponse<ApiPermission>>(`/admin/roles-permissions/permissions/${uuid}`)
  );

export const getPermissionsGroupedByCategoryAPI = () =>
  safeApi<PermissionsGroupedByCategory>(() =>
    apiClient.get<ApiResponse<PermissionsGroupedByCategory>>('/admin/roles-permissions/permissions/grouped-by-category')
  );

export const createPermissionAPI = (permissionData: CreatePermissionRequest) =>
  safeApi<ApiPermission>(() =>
    apiClient.post<ApiResponse<ApiPermission>>('/admin/roles-permissions/permissions', permissionData)
  );

export const updatePermissionAPI = (uuid: string, permissionData: UpdatePermissionRequest) =>
  safeApi<ApiPermission>(() =>
    apiClient.put<ApiResponse<ApiPermission>>(`/admin/roles-permissions/permissions/${uuid}`, permissionData)
  );

export const deletePermissionAPI = (uuid: string) =>
  safeApi<void>(() =>
    apiClient.delete<ApiResponse<void>>(`/admin/roles-permissions/permissions/${uuid}`)
  );

// ===============================
// UTILITY API FUNCTIONS
// ===============================

export const checkUserPermissionAPI = (checkData: CheckPermissionRequest) =>
  safeApi<CheckPermissionResponse>(() =>
    apiClient.post<ApiResponse<CheckPermissionResponse>>('/admin/roles-permissions/check-permission', checkData)
  );

// ===============================
// HELPER FUNCTIONS
// ===============================

/**
 * Convert API permission to legacy Permission format for backward compatibility
 */
export const convertApiPermissionToLegacy = (apiPermission: ApiPermission): import('../types/permissions').Permission => {
  return {
    id: apiPermission.action,
    name: apiPermission.name,
    description: apiPermission.description || '',
    category: apiPermission.category as import('../types/permissions').PermissionCategory,
    isDefault: apiPermission.isDefault,
  };
};

/**
 * Convert API permissions array to legacy format grouped by category
 */
export const convertApiPermissionsToLegacyGrouped = (apiPermissions: ApiPermission[]) => {
  const legacyPermissions = apiPermissions.map(convertApiPermissionToLegacy);
  return legacyPermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {} as Record<import('../types/permissions').PermissionCategory, import('../types/permissions').Permission[]>);
};

/**
 * Get role permissions in legacy format for backward compatibility
 */
export const getRolePermissionsLegacy = async (roleName: string): Promise<import('../types/permissions').Permission[]> => {
  try {
    // First try to get from API
    const rolesResponse = await getAllRolesAPI({ includeSystemRoles: true });
    if (rolesResponse.status) {
      const role = rolesResponse.data?.find(r => r.name === roleName);
      if (role) {
        const permissionsResponse = await getRolePermissionsAPI(role.uuid);
        if (permissionsResponse.status) {
          return permissionsResponse.data?.map(convertApiPermissionToLegacy) || [];
        }
      }
    }
    
    // Fallback to hardcoded permissions
    const { getRolePermissions } = await import('../types/permissions');
    return getRolePermissions(roleName);
  } catch (error) {
    console.warn('Failed to fetch role permissions from API, using fallback:', error);
    const { getRolePermissions } = await import('../types/permissions');
    return getRolePermissions(roleName);
  }
};